import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DepartmentApprover {
  id: string;
  department_id: string;
  user_id: string;
  approval_level: number | null;
  max_approval_amount: number | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export const useDepartmentApprovers = (departmentId?: string) => {
  return useQuery({
    queryKey: ['department-approvers', departmentId],
    queryFn: async () => {
      let query = supabase
        .from('department_approvers')
        .select('*')
        .order('approval_level', { ascending: true });
      
      if (departmentId) {
        query = query.eq('department_id', departmentId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as DepartmentApprover[];
    }
  });
};

export const useCreateDepartmentApprover = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (approver: Omit<DepartmentApprover, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('department_approvers')
        .insert(approver)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-approvers'] });
    }
  });
};

export const useUpdateDepartmentApprover = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DepartmentApprover> & { id: string }) => {
      const { data, error } = await supabase
        .from('department_approvers')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-approvers'] });
    }
  });
};

export const useDeleteDepartmentApprover = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('department_approvers')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-approvers'] });
    }
  });
};
